import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { getForatableModalUrl } from '../lib/foratable';

const AnimatedSignature = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const text = "~ da daniele";

  useEffect(() => {
    // Start signature after title animation (delay 1.5s after page load)
    const timer1 = setTimeout(() => setIsVisible(true), 1500);
    const timer2 = setTimeout(() => setShowSignature(true), 1800);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <span 
      className={`font-signature text-2xl md:text-3xl block mt-3 transition-opacity duration-500 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        fontStyle: 'italic'
      }}
    >
      {text.split('').map((char, index) => (
        <span
          key={index}
          className="inline-block transition-all duration-300"
          style={{
            background: 'linear-gradient(90deg, #fcd34d, #f59e0b, #d97706)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            opacity: showSignature ? 1 : 0,
            transform: showSignature ? 'translateY(0) rotate(0deg)' : 'translateY(10px) rotate(-5deg)',
            transitionDelay: `${index * 80}ms`,
            filter: showSignature ? 'drop-shadow(0 0 8px rgba(251, 191, 36, 0.5))' : 'none'
          }}
        >
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </span>
  );
};

const Hero = () => {
  const { t, language } = useLanguage();
  const foratableModalUrl = getForatableModalUrl(language);
  const [isLoaded, setIsLoaded] = useState(false);
  const [lockedHeight, setLockedHeight] = useState(null);

  useEffect(() => {
    setIsLoaded(true);
    // Mobile Chrome / Google App fix: capture the initial viewport height
    // ONCE at mount and lock the hero section to it. This prevents the URL
    // bar show/hide from triggering a viewport resize that visually "zooms"
    // the background image (object-cover recalculates the crop on resize).
    // CSS `min-h-svh` already helps, this JS lock is an extra belt-and-suspenders.
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
      setLockedHeight(window.innerHeight);
    }
  }, []);
  
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen min-h-svh flex items-center justify-center overflow-hidden"
      style={lockedHeight ? { minHeight: `${lockedHeight}px`, height: `${lockedHeight}px` } : undefined}
    >
      {/* Background Image - Fixed */}
      <div className="absolute inset-0">
        <img
          src="/images/restaurant/trattoria_da_daniele_003.jpg"
          alt="Sala interna della Trattoria Pizzeria Da Daniele a Solothurn"
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-stone-950/70 via-stone-950/50 to-stone-950/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/60 to-transparent" />
      </div>

      {/* Animated Decorative Elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-amber-600/10 rounded-full blur-3xl animate-pulse-glow" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-700/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }} />
      
      {/* Floating decorative circles */}
      <div className="absolute top-1/4 right-1/4 w-4 h-4 bg-amber-500/30 rounded-full animate-float" style={{ animationDelay: '0.5s' }} />
      <div className="absolute bottom-1/3 left-1/4 w-3 h-3 bg-amber-400/30 rounded-full animate-float" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 right-1/3 w-2 h-2 bg-amber-300/30 rounded-full animate-float" style={{ animationDelay: '1.5s' }} />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Subtitle */}
        <p className={`text-amber-400 uppercase tracking-[0.3em] text-sm md:text-base mb-4 transition-all duration-1000 ${
          isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          {t('hero.welcome')}
        </p>

        {/* Main Title */}
        <h1 className={`font-serif text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 transition-all duration-1000 delay-200 ${
          isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <span className="block">{t('hero.title')}</span>
          <span className="block text-amber-200 italic animate-text-shimmer bg-gradient-to-r from-amber-100 via-amber-300 to-amber-100 bg-[length:200%_auto] bg-clip-text text-transparent">
            {t('hero.subtitle')}
          </span>
          <AnimatedSignature />
        </h1>

        {/* Description */}
        <p className={`text-lg md:text-xl text-amber-100/80 max-w-2xl mx-auto mb-10 transition-all duration-1000 delay-400 ${
          isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          {t('hero.description')}
        </p>

        {/* CTA Buttons */}
        <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-1000 delay-600 ${
          isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <Button
            onClick={() => scrollToSection('#menu')}
            className="btn-animated ripple bg-amber-700 hover:bg-amber-600 text-white px-8 py-6 rounded-full text-lg font-medium shadow-lg shadow-amber-900/30 hover:shadow-amber-600/40 hover:scale-105 transition-all duration-300"
          >
            {t('hero.cta')}
          </Button>
          <Button
            data-lunchgate-modal-url={foratableModalUrl}
            data-testid="hero-reserve-button"
            variant="outline"
            className="border-2 border-amber-200/50 text-amber-100 hover:bg-amber-200/10 hover:border-amber-200 px-8 py-6 rounded-full text-lg font-medium transition-all bg-transparent hover:scale-105"
          >
            {t('hero.secondaryCta')}
          </Button>
        </div>

        {/* Scroll Indicator */}
        <button
          onClick={() => scrollToSection('#about')}
          className={`absolute bottom-10 left-1/2 -translate-x-1/2 text-amber-200/60 hover:text-amber-200 transition-all duration-300 hover:scale-110 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: '1s' }}
        >
          <ChevronDown className="w-8 h-8 animate-bounce" />
        </button>
      </div>
    </section>
  );
};

export default Hero;
